import React, { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { executiveMonitoringApi, type SimplifiedSiteStatus } from '../../shared/api/executive-monitoring.api'
import { useWebSocketContext } from '../real-time-updates/useWebSocketContext'

export interface SiteStats {
  working: number
  problem: number
  notWorking: number
  total: number
  uptime: string
}

export interface UseSiteStatusResult {
  sites: SimplifiedSiteStatus[]
  stats: SiteStats
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  lastUpdate: string | undefined
}

export const useSiteStatus = (): UseSiteStatusResult => {
  const { executiveData } = useWebSocketContext()
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['executive-monitoring'],
    queryFn: async () => {
      try {
        const response = await executiveMonitoringApi.getMonitoringData()
        
        console.log('API Response in useSiteStatus:', {
          response,
          hasData: !!response.data,
          dataLength: response.data?.length,
          timestamp: response.timestamp
        })
        
        return {
          success: true,
          data: response.data || [],
          timestamp: response.timestamp
        }
      } catch (err) {
        console.error('Error fetching monitoring data:', err)
        return {
          success: false,
          data: [],
          timestamp: new Date().toISOString()
        }
      }
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  })

  // Логируем WebSocket данные
  React.useEffect(() => {
    if (executiveData) {
      console.log('WebSocket executiveData received:', {
        type: typeof executiveData,
        isArray: Array.isArray(executiveData),
        data: executiveData
      })
    }
  }, [executiveData])

  // Обновляем данные при получении через WebSocket (только если есть реальные данные)
  React.useEffect(() => {
    if (executiveData && Array.isArray(executiveData) && executiveData.length > 0) {
      console.log('Updating query data with WebSocket:', {
        webSocketDataLength: executiveData.length
      })
      
      queryClient.setQueryData(['executive-monitoring'], {
        success: true,
        data: executiveData,
        timestamp: new Date().toISOString()
      })
    }
  }, [executiveData, queryClient])

  // Безопасное извлечение данных - приоритет у API данных
  const sites = useMemo(() => {
    // Проверяем данные из API
    if (data && data.success && Array.isArray(data.data) && data.data.length > 0) {
      console.log('Using API data:', { count: data.data.length })
      return data.data
    }
    
    // Проверяем данные из WebSocket
    if (executiveData && Array.isArray(executiveData) && executiveData.length > 0) {
      console.log('Using WebSocket data:', { count: executiveData.length })
      return executiveData
    }
    
    console.log('No valid data found, returning empty array')
    return []
  }, [data, executiveData])

  // Защищенный расчет статистики
  const stats = useMemo(() => {
    console.log('Calculating stats for sites:', sites)
    
    try {
      if (!Array.isArray(sites)) {
        console.error('Sites is not an array:', sites)
        return {
          working: 0,
          problem: 0,
          notWorking: 0,
          total: 0,
          uptime: '0.0'
        }
      }

      const working = sites.filter(s => s?.status === 'working').length
      const problem = sites.filter(s => s?.status === 'problem').length
      const notWorking = sites.filter(s => s?.status === 'not_working').length
      const total = sites.length

      const result = {
        working,
        problem,
        notWorking,
        total,
        uptime: total > 0 ? ((working / total) * 100).toFixed(1) : '100.0'
      }
      
      console.log('Calculated stats:', result)
      return result
    } catch (error) {
      console.error('Error calculating stats:', error)
      return {
        working: 0,
        problem: 0,
        notWorking: 0,
        total: 0,
        uptime: '0.0'
      }
    }
  }, [sites])

  return {
    sites,
    stats,
    isLoading,
    error: error as Error | null,
    refetch: async () => {
      await refetch()
    },
    lastUpdate: data?.timestamp
  }
}